/**
 * The coordinator's Action Center, computed from bookings plus coordinator_outreach. Pure functions.
 *
 * The rules are the coordinator's spreadsheet rules, written down (18 Sep 2026):
 *   Urgent Coverage Needed   role still open, event inside the horizon
 *   5-Day Check-In           talent assigned, event within 5 days, no check-in yet
 *   Box Folder               talent assigned, event within 5 days, no upload link yet
 *   Day-Of Check-In          event today, no day-of check-in yet
 *   OVERDUE Media/Form       more than 3 days past the event (media is due 3 days post-event, so day 4 on; Traci
 *                            28 Sep 2026: talent 1 and 2 days out were flagged overdue), no media and no job completion
 *                            form for that role, and the coordinator has not marked it received by hand
 *   Client Contact Needed    talent assigned, event within 21 days, talent has not confirmed contact with the client
 *                            (Traci's doc, Oct 2026: contact starts a month out, so it is only a problem once the
 *                            3-weeks-before mark passes). "All upcoming" still lists every upcoming one.
 *   Insurance Form Needed    insurance bought for the booking, event within 10 days, the assigned talent has not
 *                            completed their insurance form (Traci's doc, Oct 2026)
 *
 * Horizon (Trera, 18 Sep 2026): her dashboard runs on "Next 120 days" with an "All upcoming" option and
 * the Action Center follows it. So do these: horizonDays = 120 by default, null = no cap.
 *
 * Derived statuses (her vocabulary) come from the booking: Booked, 5 Day Check In, Contacted Client,
 * Completed Upload/Form, Media & Form Due. The three manual ones (Not Started, Reached Out, Listed on
 * Indeed) come from coordinator_outreach while the slot is open. N/A when the package has no such role.
 */
import {
  ROLES, ROLE_LABELS, roleOccupant, isRoseBooking, packageNeedsPhotographer, packageNeedsVideo, hasSecondShooterAddon, hasSecondVideographerAddon
} from '../booking/staffing.js'
import { daysOut } from './queues.js'

export const DEFAULT_HORIZON_DAYS = 120
export const CHECKIN_DAYS = 5
export const MEDIA_DUE_DAYS = 3
/** How far back OVERDUE Media/Form looks. Video uploads are not stamped on the booking today, so without a window every past video job would sit there forever. */
export const MEDIA_WINDOW_DAYS = 30
export const INSURANCE_FORM_DAYS = 10
export const CLIENT_CONTACT_DAYS = 21
/** Urgent Coverage on the usual view (Traci's Loom, 28 Sep 2026: "next 32 days which is urgent, then All Upcoming", was 120). */
export const URGENT_COVERAGE_DAYS = 32

export const ACTION_KINDS = [
  { key: 'urgent_coverage', label: 'Urgent Coverage Needed', tone: 'red' },
  { key: 'five_day_checkin', label: '5-Day Check-In Needed', tone: 'amber' },
  { key: 'day_of_checkin', label: 'Day-Of Check-In Due', tone: 'amber' },
  { key: 'box_folder', label: 'Box Folder Needed', tone: 'amber' },
  { key: 'insurance_form', label: 'Insurance Form Needed', tone: 'amber' },
  { key: 'media_overdue', label: 'OVERDUE Media/Form', tone: 'purple' },
  { key: 'client_contact', label: 'Client Contact Needed', tone: 'yellow' }
]
const KIND_ORDER = Object.fromEntries(ACTION_KINDS.map((k, i) => [k.key, i]))

/**
 * The window each card covers, for the switch above its list (Traci's doc, Oct 2026: "can we have the 'next 120' days
 * button to change what is said depending on which card it's on"). `toggle` cards switch between their window and All
 * upcoming; the others have a fixed window by rule, so they show it as a plain label.
 */
export function cardWindow(kind) {
  switch (kind) {
    case 'client_contact': return { toggle: true, label: `Next ${CLIENT_CONTACT_DAYS} days` }
    case 'urgent_coverage': return { toggle: true, label: `Next ${URGENT_COVERAGE_DAYS} days` }
    case 'five_day_checkin': return { toggle: false, label: `Next ${CHECKIN_DAYS} days` }
    case 'box_folder': return { toggle: false, label: `Next ${CHECKIN_DAYS} days` }
    case 'day_of_checkin': return { toggle: false, label: 'Today' }
    case 'insurance_form': return { toggle: false, label: `Next ${INSURANCE_FORM_DAYS} days` }
    case 'media_overdue': return { toggle: false, label: `${MEDIA_DUE_DAYS + 1}–${MEDIA_WINDOW_DAYS} days after the event` }
    default: return { toggle: true, label: `Next ${DEFAULT_HORIZON_DAYS} days` }
  }
}

export const MANUAL_STATUSES = [
  { key: 'not_started', label: 'Not Started' },
  { key: 'reached_out', label: 'Reached Out' },
  { key: 'listed_on_indeed', label: 'Listed on Indeed' }
]
export const LEAD_SOURCES = [
  { key: 'site', label: 'Our site' },
  { key: 'indeed', label: 'Indeed' },
  { key: 'other', label: 'Elsewhere' }
]
export const LEAD_OUTCOMES = [
  { key: 'no_reply', label: 'No reply' },
  { key: 'declined', label: 'Declined' },
  { key: 'booked', label: 'Booked' },
  { key: 'withdrawn', label: 'Withdrawn' }
]

const ROLE_SHORT = { photographer: 'Photo', videographer: 'Video', second_photographer: '2nd Photo', second_videographer: '2nd Video' }
export const roleShort = (role) => ROLE_SHORT[role] || role

/** Does this booking's package call for the role at all? */
export function roleApplies(booking, role) {
  if (!booking || isRoseBooking(booking)) return false
  if (role === 'photographer') return packageNeedsPhotographer(booking)
  if (role === 'videographer') return packageNeedsVideo(booking)
  if (role === 'second_photographer') return hasSecondShooterAddon(booking)
  if (role === 'second_videographer') return hasSecondVideographerAddon(booking)
  return false
}

const hasUploadLink = (b) => Boolean(b.photos_upload_link || b.videos_upload_link || b.raws_upload_link || b.media_upload_url)

/** The role a job completion form belongs to (older forms have no talent_role: the photographer's). */
export const formRole = (form) => (ROLES.includes(form?.talent_role) ? form.talent_role : 'photographer')

/** job_completion_forms rows → booking_id -> Set of roles that have submitted one. */
export function formRolesByBooking(forms) {
  const out = new Map()
  for (const f of forms || []) {
    if (!f?.booking_id) continue
    if (!out.has(f.booking_id)) out.set(f.booking_id, new Set())
    out.get(f.booking_id).add(formRole(f))
  }
  return out
}

/**
 * Is this role's media in? Any of:
 *   - the booking's upload stamps (photo and 2nd photo share the photo stamps; video has its own)
 *   - a job completion form submitted for this role (24 Sep 2026: the form never sets the stamps, so seven finished
 *     jobs with forms on file still showed as overdue)
 *   - the coordinator marked it received by hand (talent ghosted, or used the old form)
 *   - Editing already has it: a media link saved on the booking, a review started, or the gallery delivered
 *     (27 Sep 2026: anything that shows the media reached us counts, so nobody who uploaded is chased for it).
 *     "pending_upload" is every booking's starting review status, so it is not a sign of anything.
 */
const NOT_STARTED = new Set(['', 'pending_upload', 'not_started'])
const reviewStarted = s => Boolean(s) && !NOT_STARTED.has(String(s).toLowerCase())

export function mediaUploadedFor(booking, role, { formRoles = null, outreach = null } = {}) {
  if (!booking) return false
  if (outreach?.media_received_at) return true
  if (formRoles && formRoles.has(role)) return true
  if (booking.media_uploaded_at || booking.photos_uploaded || booking.media_delivered_at || booking.media_upload_url) return true
  const m = role === 'videographer' || role === 'second_videographer' ? 'video' : 'photo'
  return Boolean(booking[`${m}_uploaded_at`] || booking[`${m}_media_url`] || reviewStarted(booking[`${m}_review_status`]) || booking[`${m}_delivered_at`])
}

/**
 * Her status word for one role on one booking.
 * outreach = the coordinator_outreach row for (booking, role) or null.
 */
export function roleStatus(booking, role, outreach, today, formRoles = null) {
  if (!roleApplies(booking, role)) return { key: 'na', label: 'N/A' }
  const occ = roleOccupant(booking, role)
  const d = daysOut(booking.event_date, today)
  if (occ.kind !== 'talent') {
    const s = outreach?.status || 'not_started'
    return { key: s, label: MANUAL_STATUSES.find((m) => m.key === s)?.label || s }
  }
  if (d < 0) {
    return mediaUploadedFor(booking, role, { formRoles, outreach }) ? { key: 'completed', label: 'Completed Upload/Form' } : { key: 'media_due', label: 'Media & Form Due' }
  }
  if (booking.client_outreach_confirmed) return { key: 'contacted_client', label: 'Contacted Client' }
  if (booking.talent_confirmed_seen || outreach?.five_day_checked_in_at) return { key: 'five_day', label: '5 Day Check In' }
  return { key: 'booked', label: 'Booked' }
}

/**
 * Action Center items for a set of bookings. `outreachByKey` maps `${booking_id}:${role}` → row.
 * `talentName(id)` resolves a photographers.id to a display name (may return null).
 * `formsByBooking` maps booking_id → Set of roles with a job completion form (formRolesByBooking).
 */
/**
 * insurance_forms rows the talent still owes: `${booking_id}:${talent_id}` -> row. A completed, purchased or resolved
 * form is not owed. talent_id is the booking's slot id (photographers.id).
 */
export function openInsuranceForms(rows) {
  const out = new Map()
  for (const f of rows || []) {
    const admin = String(f?.admin_action_status || '').toLowerCase()
    if (!f?.booking_id || !f?.talent_id || f.coi_purchased || String(f.form_status || '').toLowerCase() === 'completed' || admin === 'purchased' || admin === 'resolved') continue
    out.set(`${f.booking_id}:${f.talent_id}`, f)
  }
  return out
}

export function buildActionCenter(bookings, { outreachByKey = new Map(), today, talentName = () => null, horizonDays = DEFAULT_HORIZON_DAYS, formsByBooking = new Map(), insuranceByKey = new Map() } = {}) {
  // Client contact: the 21-day mark on the usual view (her "Next 120 days", the Admin dashboard); every upcoming one on "All upcoming"
  const contactDue = (d) => (horizonDays === null || horizonDays === undefined ? true : d <= Math.min(horizonDays, CLIENT_CONTACT_DAYS))
  // Urgent Coverage: the next 32 days on the usual view; every upcoming open role on "All upcoming"
  const coverageDue = (d) => (horizonDays === null || horizonDays === undefined ? true : d <= Math.min(horizonDays, URGENT_COVERAGE_DAYS))
  const items = []
  for (const b of bookings || []) {
    if (!b || b.is_test || b.booking_status === 'cancelled') continue
    const d = daysOut(b.event_date, today)
    if (!Number.isFinite(d)) continue
    for (const role of ROLES) {
      if (!roleApplies(b, role)) continue
      const occ = roleOccupant(b, role)
      const outreach = outreachByKey.get(`${b.id}:${role}`) || null
      const base = { booking_id: b.id, role, role_label: ROLE_LABELS[role], role_short: roleShort(role), event_date: String(b.event_date).slice(0, 10), days_out: d }
      const name = occ.kind === 'talent' ? (talentName(occ.id) || 'talent') : null

      if (occ.kind !== 'talent') {
        if (d >= 0 && coverageDue(d)) {
          const st = roleStatus(b, role, outreach, today)
          items.push({ ...base, kind: 'urgent_coverage', what: `Event in ${d} day${d === 1 ? '' : 's'} — no ${roleShort(role).toLowerCase()} assigned (status: ${st.label.toLowerCase()})` })
        }
        continue
      }
      if (d >= 0 && d <= CHECKIN_DAYS && !b.talent_confirmed_seen && !outreach?.five_day_checked_in_at) {
        items.push({ ...base, kind: 'five_day_checkin', talent: name, what: `Check in with ${name} for their shoot in ${d} day${d === 1 ? '' : 's'}` })
      }
      if (d === 0 && !outreach?.day_of_checked_in_at) {
        items.push({ ...base, kind: 'day_of_checkin', talent: name, what: `Day-of check-in with ${name}` })
      }
      if (d >= 0 && d <= CHECKIN_DAYS && !hasUploadLink(b)) {
        items.push({ ...base, kind: 'box_folder', talent: name, what: `Create and share the Box folder for ${name}` })
      }
      if (d >= 0 && d <= INSURANCE_FORM_DAYS && insuranceByKey.has(`${b.id}:${occ.id}`)) {
        items.push({ ...base, kind: 'insurance_form', talent: name, what: `${name} has not completed the insurance form — event in ${d} day${d === 1 ? '' : 's'}` })
      }
      if (d < -MEDIA_DUE_DAYS && d >= -MEDIA_WINDOW_DAYS && !mediaUploadedFor(b, role, { formRoles: formsByBooking.get(b.id) || null, outreach })) {
        const past = -d
        items.push({ ...base, kind: 'media_overdue', talent: name, overdue: past > MEDIA_DUE_DAYS, what: `${name} is ${past} day${past === 1 ? '' : 's'} past event — media was due ${MEDIA_DUE_DAYS} days post-event` })
      }
      if (d >= 1 && contactDue(d) && !b.client_outreach_confirmed) {
        items.push({ ...base, kind: 'client_contact', talent: name, what: `Remind ${name} to contact the client` })
      }
    }
  }
  items.sort((a, b) => (KIND_ORDER[a.kind] - KIND_ORDER[b.kind]) || (a.days_out - b.days_out) || a.event_date.localeCompare(b.event_date))
  const counts = Object.fromEntries(ACTION_KINDS.map((k) => [k.key, items.filter((i) => i.kind === k.key).length]))
  return { items, counts, total: items.length }
}
